import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { toDealBadgeSegmentProps } from '@/helpers/deals/dealBadgeSegmentProps'
import type { DealBadgeSegmentProps } from '@/helpers/deals/dealBadgeSegmentProps'
import { isTopTierDeal } from '@/helpers/deals/isTopTierDeal'
import {
  NO_BADGE,
  resolveDealBadge,
  toBadgeData,
} from '@/helpers/deals/resolveDealBadge'
import type {
  DealBadgeData,
  ListingWithDeal,
} from '@/helpers/deals/resolveDealBadge'
import { FeatureFlags } from '@/helpers/featureFlags'
import type { ScreenName } from '@/helpers/telemetry/ScreenName'
import { useDealBadgeFlags } from '@/hooks/useDealBadgeFlags'

/**
 * Hook form of `resolveDealBadge` (see it for flag precedence by screen),
 * reading the flags itself.
 *
 * For LDP (Listing Details Page) - use useDealBadgeDataLDP instead
 *
 * @param listing - The listing object containing deal information
 * @returns Display properties (shouldShowDealBadge, dealLevel), and the Segment
 * properties as `segmentProps` for callers to spread into their events
 */
export const useDealBadgeData = (
  listing: ListingWithDeal | undefined,
  screenName?: ScreenName
): {
  shouldShowDealBadge: boolean
  dealLevel: DealBadgeData['dealLevel']
  segmentProps: DealBadgeSegmentProps
} => {
  const flags = useDealBadgeFlags()

  return useMemo(() => {
    const decision = resolveDealBadge(listing, screenName, flags)
    return {
      shouldShowDealBadge: decision.shouldShowDealBadge,
      dealLevel: decision.dealLevel,
      segmentProps: toDealBadgeSegmentProps(decision),
    }
  }, [listing, screenName, flags])
}

/**
 * Shared hook for LDP (Listing Details Page) deal badge data.
 * Uses ENABLE_FMV_DEAL_BADGE flag instead of ALP-specific flag.
 *
 * @param listing - The listing object containing deal information
 * @returns Object with display properties (shouldShowDealBadge, dealLevel) and tracking properties (deal_badge_value, is_deal_badge_shown)
 */
export const useDealBadgeDataLDP = (
  listing:
    | Pick<ListingWithDeal, 'deal' | 'storefrontId' | 'storefrontSlug'>
    | undefined
): DealBadgeData => {
  const ldpBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)

  return useMemo(() => {
    if (!listing) {
      return NO_BADGE
    }
    const isStorefrontExcludedById = Boolean(
      listing.storefrontId &&
      ldpBadgeFlag.excludedStorefrontIds?.includes(listing.storefrontId)
    )

    const isStorefrontExcludedBySlug = Boolean(
      listing.storefrontSlug &&
      ldpBadgeFlag.excludedStorefrontSlugs?.includes(listing.storefrontSlug)
    )

    const isExcluded = isStorefrontExcludedById || isStorefrontExcludedBySlug
    const shouldShow =
      ldpBadgeFlag.enabled && isTopTierDeal(listing.deal) && !isExcluded

    return toBadgeData(shouldShow, listing.deal)
  }, [listing, ldpBadgeFlag])
}
